/**
 * WILLIAM_ONEIL_SELL — William O'Neil / IBD-style Sell Signal Overlay
 *
 * Draws directly on the candle pane (isStack=true, paneId='candle_pane').
 *
 * A bundle of six independently toggleable, independently parameterized sell
 * rules drawn from O'Neil's CAN SLIM / IBD methodology — each is a distinct
 * heuristic, not one composite score:
 *
 *   - ma50Break        — close crosses below a key (default 50-bar) moving
 *                         average on above-average volume
 *   - ma200Break        — close crosses below the long-term (default
 *                         200-bar) moving average
 *   - climaxTop         — the largest single-bar % gain of the advance,
 *                         on the heaviest volume of the advance, after an
 *                         extended prior run
 *   - exhaustionGap     — a large gap-up on heavy volume after an extended
 *                         prior advance
 *   - distributionDays  — heavy-volume down days clustering within a
 *                         trailing window (individual days + a stronger
 *                         "threshold reached" event)
 *   - pivotStop         — a 7-8%-style stop-loss line measured from the
 *                         most recent *confirmed* breakout pivot, not a
 *                         manual cost basis — see conversation: O'Neil's own
 *                         rule is to buy at the pivot, so the stop is
 *                         naturally derivable from price action alone.
 */

import { IndicatorSeries, type Bounding, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import type {
  WilliamOneilSellSettings,
  Ma50BreakRuleSettings,
  Ma200BreakRuleSettings,
  ClimaxTopRuleSettings,
  ExhaustionGapRuleSettings,
  DistributionDaysRuleSettings,
  PivotStopRuleSettings,
} from '@/types';

// ── Types ───────────────────────────────────────────────────────────────────

type KBar = { timestamp?: number; open: number; high: number; low: number; close: number; volume?: number | null };

export type SellRule = 'ma50Break' | 'ma200Break' | 'climaxTop' | 'exhaustionGap' | 'distributionDay' | 'distributionThreshold';

export interface SellEvent {
  index: number;
  rule: SellRule;
  label: string;
}

export interface PivotStopLine {
  pivotIndex: number;
  pivotPrice: number;
  stopPrice: number;
}

export interface WilliamOneilSellResult {
  events: SellEvent[];
  pivotStop: PivotStopLine | null;
}

export const DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS: WilliamOneilSellSettings = {
  ma50Break:        { enabled: true, maPeriod: 50, volMultiplier: 1.4, avgVolPeriod: 50 },
  ma200Break:       { enabled: true, maPeriod: 200 },
  climaxTop:        { enabled: true, lookbackBars: 252, minAdvancePct: 0.25, volMultiplier: 1.5 },
  exhaustionGap:    { enabled: true, minGapPct: 0.02, minAdvancePct: 0.25, advanceLookbackBars: 60, volMultiplier: 1.5 },
  distributionDays: { enabled: true, windowBars: 25, minDownPct: 0.002, volMultiplier: 1.0, thresholdCount: 5 },
  pivotStop:        { enabled: true, stopPct: 0.075, pivotLookbackBars: 130, pivotBars: 9 },
};

// ── Small numeric helpers ──────────────────────────────────────────────────

function sma(values: number[], period: number): (number | null)[] {
  const n = values.length;
  const out = new Array<number | null>(n).fill(null);
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

function findPivotHighs(highs: number[], pivotBars: number): boolean[] {
  const n = highs.length;
  const pivotHigh = new Array<boolean>(n).fill(false);
  for (let i = pivotBars; i < n - pivotBars; i++) {
    let isHigh = true;
    for (let k = i - pivotBars; k <= i + pivotBars; k++) {
      if (k === i) continue;
      if (highs[k] > highs[i]) { isHigh = false; break; }
    }
    pivotHigh[i] = isHigh;
  }
  return pivotHigh;
}

// ── Rule detectors ──────────────────────────────────────────────────────────

function detectMa50Break(bars: KBar[], s: Ma50BreakRuleSettings): SellEvent[] {
  if (!s.enabled) return [];
  const closes = bars.map((b) => b.close);
  const vols = bars.map((b) => b.volume ?? 0);
  const ma = sma(closes, s.maPeriod);
  const avgVol = sma(vols, s.avgVolPeriod);
  const events: SellEvent[] = [];
  for (let i = 1; i < bars.length; i++) {
    const maPrev = ma[i - 1], maCur = ma[i];
    if (maPrev == null || maCur == null) continue;
    if (!(closes[i - 1] >= maPrev && closes[i] < maCur)) continue;
    const av = avgVol[i];
    if (av == null || av <= 0 || vols[i] < av * s.volMultiplier) continue;
    events.push({ index: i, rule: 'ma50Break', label: `Below ${s.maPeriod}MA on volume` });
  }
  return events;
}

function detectMa200Break(bars: KBar[], s: Ma200BreakRuleSettings): SellEvent[] {
  if (!s.enabled) return [];
  const closes = bars.map((b) => b.close);
  const ma = sma(closes, s.maPeriod);
  const events: SellEvent[] = [];
  for (let i = 1; i < bars.length; i++) {
    const maPrev = ma[i - 1], maCur = ma[i];
    if (maPrev == null || maCur == null) continue;
    if (closes[i - 1] >= maPrev && closes[i] < maCur) {
      events.push({ index: i, rule: 'ma200Break', label: `Below ${s.maPeriod}MA` });
    }
  }
  return events;
}

function detectClimaxTop(bars: KBar[], s: ClimaxTopRuleSettings): SellEvent[] {
  if (!s.enabled) return [];
  const n = bars.length;
  const closes = bars.map((b) => b.close);
  const vols = bars.map((b) => b.volume ?? 0);
  const events: SellEvent[] = [];
  for (let i = 1; i < n; i++) {
    const windowStart = Math.max(1, i - s.lookbackBars + 1);
    if (i - windowStart < 4) continue;

    let lowInWindow = Infinity;
    for (let j = windowStart - 1; j < i; j++) if (closes[j] < lowInWindow) lowInWindow = closes[j];
    if (lowInWindow <= 0) continue;
    const advancePct = (closes[i - 1] - lowInWindow) / lowInWindow;
    if (advancePct < s.minAdvancePct) continue;

    let maxGain = -Infinity;
    let maxVol = -Infinity;
    let sumVol = 0, count = 0;
    for (let j = windowStart; j <= i; j++) {
      const gain = (closes[j] - closes[j - 1]) / closes[j - 1];
      if (gain > maxGain) maxGain = gain;
      if (vols[j] > maxVol) maxVol = vols[j];
      if (j < i) { sumVol += vols[j]; count++; }
    }
    const todayGain = (closes[i] - closes[i - 1]) / closes[i - 1];
    if (todayGain <= 0 || todayGain < maxGain) continue; // not the largest gain of the move
    if (vols[i] < maxVol) continue; // not the heaviest volume of the move

    const avgVol = count > 0 ? sumVol / count : 0;
    if (avgVol <= 0 || vols[i] < avgVol * s.volMultiplier) continue;

    events.push({ index: i, rule: 'climaxTop', label: 'Climax top' });
  }
  return events;
}

function detectExhaustionGap(bars: KBar[], s: ExhaustionGapRuleSettings): SellEvent[] {
  if (!s.enabled) return [];
  const n = bars.length;
  const closes = bars.map((b) => b.close);
  const vols = bars.map((b) => b.volume ?? 0);
  const events: SellEvent[] = [];
  for (let i = 1; i < n; i++) {
    const gapPct = (bars[i].open - closes[i - 1]) / closes[i - 1];
    if (gapPct < s.minGapPct) continue;

    const windowStart = Math.max(0, i - s.advanceLookbackBars);
    if (i - windowStart < 4) continue;
    let lowInWindow = Infinity;
    let sumVol = 0, count = 0;
    for (let j = windowStart; j < i; j++) {
      if (closes[j] < lowInWindow) lowInWindow = closes[j];
      sumVol += vols[j];
      count++;
    }
    if (lowInWindow <= 0) continue;
    const advancePct = (closes[i - 1] - lowInWindow) / lowInWindow;
    if (advancePct < s.minAdvancePct) continue;

    const avgVol = count > 0 ? sumVol / count : 0;
    if (avgVol <= 0 || vols[i] < avgVol * s.volMultiplier) continue;

    events.push({ index: i, rule: 'exhaustionGap', label: 'Exhaustion gap' });
  }
  return events;
}

function detectDistributionDays(bars: KBar[], s: DistributionDaysRuleSettings): SellEvent[] {
  if (!s.enabled) return [];
  const n = bars.length;
  const closes = bars.map((b) => b.close);
  const vols = bars.map((b) => b.volume ?? 0);
  const events: SellEvent[] = [];
  const isDistribution = new Array<boolean>(n).fill(false);

  for (let i = 1; i < n; i++) {
    const changePct = (closes[i] - closes[i - 1]) / closes[i - 1];
    if (changePct > -s.minDownPct) continue;
    if (vols[i - 1] <= 0 || vols[i] < vols[i - 1] * s.volMultiplier) continue;
    isDistribution[i] = true;
    events.push({ index: i, rule: 'distributionDay', label: 'Distribution day' });
  }

  // Rolling count within the window — emit a stronger event only the bar the
  // threshold is first reached, not on every bar it stays elevated.
  let wasAtThreshold = false;
  for (let i = 0; i < n; i++) {
    const windowStart = Math.max(0, i - s.windowBars + 1);
    let count = 0;
    for (let j = windowStart; j <= i; j++) if (isDistribution[j]) count++;
    const atThreshold = count >= s.thresholdCount;
    if (atThreshold && !wasAtThreshold) {
      events.push({ index: i, rule: 'distributionThreshold', label: `${count} distribution days in ${s.windowBars} sessions` });
    }
    wasAtThreshold = atThreshold;
  }
  return events;
}

function detectPivotStop(bars: KBar[], s: PivotStopRuleSettings): PivotStopLine | null {
  if (!s.enabled) return null;
  const n = bars.length;
  if (n < s.pivotBars * 2 + 1) return null;
  const highs = bars.map((b) => b.high);
  const closes = bars.map((b) => b.close);
  const pivotHigh = findPivotHighs(highs, s.pivotBars);

  const searchStart = Math.max(s.pivotBars, n - s.pivotLookbackBars);
  // Most recent pivot high that price has since closed above — i.e. a
  // confirmed breakout, the O'Neil-defined "buy point" the stop is measured from.
  for (let i = n - 1 - s.pivotBars; i >= searchStart; i--) {
    if (!pivotHigh[i]) continue;
    const pivotPrice = highs[i];
    let confirmed = false;
    for (let j = i + 1; j < n; j++) {
      if (closes[j] > pivotPrice) { confirmed = true; break; }
    }
    if (!confirmed) continue;
    return { pivotIndex: i, pivotPrice, stopPrice: pivotPrice * (1 - s.stopPct) };
  }
  return null;
}

export function computeWilliamOneilSell(bars: KBar[], settings: WilliamOneilSellSettings): WilliamOneilSellResult {
  const events: SellEvent[] = [
    ...detectMa50Break(bars, settings.ma50Break),
    ...detectMa200Break(bars, settings.ma200Break),
    ...detectClimaxTop(bars, settings.climaxTop),
    ...detectExhaustionGap(bars, settings.exhaustionGap),
    ...detectDistributionDays(bars, settings.distributionDays),
  ];
  events.sort((a, b) => a.index - b.index);
  const pivotStop = detectPivotStop(bars, settings.pivotStop);
  return { events, pivotStop };
}

// ── Drawing ─────────────────────────────────────────────────────────────────

const RULE_STYLE: Record<SellRule, { color: string; shape: 'triangle' | 'dot'; shortLabel: string }> = {
  ma50Break:             { color: '#F97316', shape: 'triangle', shortLabel: '50MA' },
  ma200Break:            { color: '#DC2626', shape: 'triangle', shortLabel: '200MA' },
  climaxTop:             { color: '#EF4444', shape: 'triangle', shortLabel: 'CLIMAX' },
  exhaustionGap:         { color: '#B91C1C', shape: 'triangle', shortLabel: 'GAP' },
  distributionDay:       { color: '#FCA5A5', shape: 'dot', shortLabel: '' },
  distributionThreshold: { color: '#7C3AED', shape: 'triangle', shortLabel: 'DIST' },
};

const PIVOT_STOP_COLOR = 'rgba(239,68,68,0.85)';

function isLightTheme(): boolean {
  try { return useChartStore.getState().theme === 'light'; } catch { return false; }
}

function pivotLineColor(): string {
  return isLightTheme() ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)';
}

function pivotMarkerFillColor(): string {
  return isLightTheme() ? '#111827' : '#FFFFFF';
}

function pivotLabelColor(): string {
  return isLightTheme() ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)';
}

// ── Shared layout store for hover/tap-to-pin detection (written each draw) ──
// Mirrors indicators/eventMarkers.ts's eventMarkerLayouts — a module-scope
// mutable array kept in sync with the canvas because it's rebuilt every draw
// pass, read directly by ChartContainer's mousemove/pointerup hit-testing.
export type SellMarkerLayout = {
  index: number;
  events: SellEvent[];
  timestamp: number;
  x: number;
  /** top pixel of the topmost marker in the stack */
  topY: number;
  /** bottom pixel of the bottommost marker */
  bottomY: number;
};

export const sellMarkerLayouts: SellMarkerLayout[] = [];

export function formatSellMarkerDate(tsMs: number): string {
  const d = new Date(tsMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const STACK_STEP = 16; // vertical px between stacked markers when >1 event shares a bar

type DrawParams = IndicatorDrawParams<WilliamOneilSellResult> & {
  ctx: CanvasRenderingContext2D;
  kLineDataList: KBar[];
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  yAxis: { convertToPixel: (v: number) => number };
  bounding: Bounding;
};

type TooltipParams = {
  indicator: { result: WilliamOneilSellResult[] };
  crosshair: { dataIndex?: number };
};

const williamOneilSell: IndicatorTemplate<WilliamOneilSellResult> = {
  name: 'WILLIAM_ONEIL_SELL',
  shortName: "O'Neil Sell",
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  figures: [],

  calc: (dataList) => {
    const bars = dataList as unknown as KBar[];
    const settings = useChartStore.getState().williamOneilSellSettings;
    const result = computeWilliamOneilSell(bars, settings);
    return bars.map(() => result);
  },

  draw: (params: unknown): boolean => {
    const { ctx, kLineDataList, visibleRange, xAxis, yAxis, indicator } = params as DrawParams;
    const results = indicator.result;
    if (!results.length) return true;
    const { events, pivotStop } = results[results.length - 1];
    const { from, to } = visibleRange;

    ctx.save();

    // Group visible events by bar so multiple rules firing on the same bar
    // stack vertically instead of overlapping, and so hover hit-testing has
    // one combined box per bar rather than per-event.
    sellMarkerLayouts.length = 0;
    const byIndex = new Map<number, SellEvent[]>();
    for (const evt of events) {
      if (evt.index < from || evt.index > to) continue;
      const arr = byIndex.get(evt.index);
      if (arr) arr.push(evt); else byIndex.set(evt.index, [evt]);
    }

    for (const [idx, evtsAtBar] of byIndex) {
      const bar = kLineDataList[idx];
      if (!bar) continue;
      const x = xAxis.convertToPixel(idx);
      const baseY = yAxis.convertToPixel(bar.high);

      evtsAtBar.forEach((evt, k) => {
        const yTop = baseY - k * STACK_STEP;
        const style = RULE_STYLE[evt.rule];
        if (style.shape === 'dot') {
          ctx.fillStyle = style.color;
          ctx.beginPath();
          ctx.arc(x, yTop - 6, 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = style.color;
          ctx.beginPath();
          ctx.moveTo(x - 5, yTop - 8);
          ctx.lineTo(x + 5, yTop - 8);
          ctx.lineTo(x, yTop - 2);
          ctx.closePath();
          ctx.fill();
          ctx.font = 'bold 9px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(style.shortLabel, x, yTop - 9);
        }
      });

      sellMarkerLayouts.push({
        index: idx,
        events: evtsAtBar,
        timestamp: bar.timestamp ?? 0,
        x,
        topY: baseY - (evtsAtBar.length - 1) * STACK_STEP - 8,
        bottomY: baseY - 2,
      });
    }

    if (pivotStop && pivotStop.pivotIndex <= to) {
      const xPivot = xAxis.convertToPixel(pivotStop.pivotIndex);
      const yPivot = yAxis.convertToPixel(pivotStop.pivotPrice);
      const xStart = xAxis.convertToPixel(Math.max(pivotStop.pivotIndex, from));
      const xEnd = xAxis.convertToPixel(to);

      // ── Pivot level: dotted reference line + a marker at the exact
      // confirmed pivot bar, so it's visually traceable what the stop below
      // is measured from — not just a floating number.
      ctx.strokeStyle = pivotLineColor();
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(xStart, yPivot);
      ctx.lineTo(Math.max(xStart, xEnd), yPivot);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = pivotMarkerFillColor();
      ctx.beginPath();
      ctx.arc(xPivot, yPivot, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = PIVOT_STOP_COLOR;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.fillStyle = pivotLabelColor();
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`Pivot ${pivotStop.pivotPrice.toFixed(2)}`, xEnd - 2, yPivot - 2);

      // ── Stop line ────────────────────────────────────────────────────
      const yStop = yAxis.convertToPixel(pivotStop.stopPrice);
      ctx.strokeStyle = PIVOT_STOP_COLOR;
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 3]);
      ctx.beginPath();
      ctx.moveTo(xStart, yStop);
      ctx.lineTo(Math.max(xStart, xEnd), yStop);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = PIVOT_STOP_COLOR;
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`Stop ${pivotStop.stopPrice.toFixed(2)}`, xEnd - 2, yStop - 2);
    }

    ctx.restore();
    return true;
  },

  createTooltipDataSource: (params: unknown) => {
    const p = params as TooltipParams;
    const result = p.indicator.result[p.indicator.result.length - 1];
    if (!result) return { name: "O'Neil Sell", calcParamsText: '', icons: [], values: [] };

    const values: { title: string; value: string }[] = [];
    const idx = p.crosshair.dataIndex;
    if (idx != null) {
      for (const evt of result.events) {
        if (evt.index === idx) values.push({ title: `${evt.label}: `, value: '⚠' });
      }
    }
    if (result.pivotStop) {
      values.push({ title: 'Pivot stop: ', value: result.pivotStop.stopPrice.toFixed(2) });
    }
    return { name: "O'Neil Sell", calcParamsText: '', icons: [], values };
  },
};

export default williamOneilSell;
